// ============================================================
// SharedCalendarCard.js — the per-client shared leave calendar (item 24).
//
// RULED (Zuber, 2026-08-26): ONE-WAY ONLY — CareHub writes, an Outlook edit
// never flows back. A GATED feature needing access to the client's tenant.
//
// 🔴 THIS CARD REPORTS THE PLATFORM GATES AS WELL AS THE PER-CLIENT ONE, and
// that is the point rather than decoration. Three things must all be true
// before a single event is written — the kill switch, the tenant allowlist and
// this row — and an operator who can only see the third has no way to tell
// "nobody enabled this client" from "the platform switch is still false". A
// page that shows only the piece it owns is how a correct configuration reads
// as broken.
//
// The kill switch and allowlist are deliberate main.bicep commits and are NOT
// editable here, matching the Platform-03 precedent: activation is a reviewed
// line in the template, never a portal edit that the next deploy reverts.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import Field from '../../components/Field';
import Switch from '../../components/Switch';
import { toast } from '../../components/Toaster';
import {
  getClientSharedCalendar,
  putClientSharedCalendar,
  sharedCalendarAction,
} from '../../api/adminClientConfig';
import { translateError } from '../../utils/errorMessages';
import StatusMessage from '../../components/StatusMessage';

export default function SharedCalendarCard({ clientId }) {
  const [enabled, setEnabled] = useState(false);
  const [mailbox, setMailbox] = useState('');
  const [gates, setGates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);        // 'test' | 'preview' | 'commit'
  const [probe, setProbe] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await getClientSharedCalendar(clientId);
      setEnabled(!!res.enabled);
      setMailbox(res.calendarMailbox || '');
      setGates(res);
    } catch (err) { setError(translateError(err)); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { fetchData(); }, [fetchData]);

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await putClientSharedCalendar(clientId, {
        enabled,
        calendarMailbox: mailbox.trim() || null,
      });
      toast.success('Shared calendar settings saved');
      // Re-read rather than trusting local state: the gate summary and the
      // mirrored-leave counts are server-derived and both move on a save.
      setProbe(null); setProposal(null);
      await fetchData();
    } catch (err) { toast.error(translateError(err)); }
    finally { setSaving(false); }
  }

  async function runAction(kind, body) {
    setBusy(kind);
    try {
      const res = await sharedCalendarAction(clientId, body);
      return res;
    } catch (err) { toast.error(translateError(err)); return null; }
    finally { setBusy(null); }
  }

  async function testConnection() {
    const res = await runAction('test', { action: 'test_connection' });
    if (!res) return;
    setProbe(res.result);
    if (res.result && res.result.reachable) toast.success('Mailbox reached');
    else toast.error('That mailbox could not be found in the client tenant');
  }

  async function previewBackfill() {
    const res = await runAction('preview', { action: 'backfill' });
    if (res) setProposal(res);
  }

  async function commitBackfill() {
    const res = await runAction('commit', { action: 'backfill', commit: true });
    if (!res) return;
    toast.success(`Back-fill complete: ${res.created} added, ${res.skipped} skipped, ${res.failed} failed`);
    setProposal(null);
    await fetchData();
  }

  if (loading) return <StatusMessage as="p" variant="loading" className="pc-state">Loading…</StatusMessage>;
  if (error) return <StatusMessage as="p" variant="error" className="pc-state pc-state--error">{error}</StatusMessage>;

  const platformReady = gates
    && gates.platformEnabled && gates.appRegistrationConfigured && gates.tenantAllowlisted;

  return (
    <div className="pc-form">
      {gates && !platformReady && (
        <StatusMessage as="p" variant="error" className="pc-state pc-state--error" data-testid="shared-calendar-gates">
          {'Platform gates not open: '}
          {[
            !gates.platformEnabled && 'the kill switch (sharedCalendarEnabled) is off',
            !gates.appRegistrationConfigured && 'no app registration is configured',
            !gates.tenantAllowlisted && (gates.externalTenantId
              ? 'this tenant is not in the allowlist'
              : 'this client has no active federation config, and the shared calendar needs a tenant relationship'),
          ].filter(Boolean).join('; ')}
          {'. These are main.bicep changes, not settings on this page. See docs/operations/shared-calendar-setup.md.'}
        </StatusMessage>
      )}

      <form onSubmit={submit}>
        <Field
          label="Shared calendar enabled"
          help="The per-client gate. Off = CareHub writes nothing to this client's calendar, whatever else is configured. Turning it OFF does not remove entries that are already there - it stops new writes, so a team's calendar is never silently erased by unticking a box."
        >
          <Switch checked={enabled} onChange={setEnabled} ariaLabel="Shared calendar enabled" />
        </Field>
        <Field
          label="Shared mailbox address"
          required={enabled}
          help="The shared mailbox in the CLIENT's tenant that holds the calendar, e.g. leave@theircompany.co.uk. It must already exist, be scoped to this app with Exchange App RBAC, and be granted Reviewer (read-only) to staff - that permission is what makes the one-way rule real."
        >
          <input
            type="text"
            maxLength={320}
            value={mailbox}
            placeholder="leave@theircompany.co.uk"
            onChange={(e) => setMailbox(e.target.value)}
            data-testid="shared-calendar-mailbox"
          />
        </Field>
        <div className="pc-actions">
          <button type="submit" className="pc-btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>

      {gates && gates.upcomingLeave && (
        <StatusMessage as="p" variant="empty" className="pc-state" data-testid="shared-calendar-counts">
          {`Approved leave not yet ended: ${gates.upcomingLeave.synced} on the calendar, ${gates.upcomingLeave.notSynced} not.`}
        </StatusMessage>
      )}
      {gates && gates.upcomingLeave === null && (
        // Absence, not zero — an operator must be able to tell "nothing is
        // synced" from "we could not find out".
        <StatusMessage as="p" variant="error" className="pc-state">Could not read the leave counts just now.</StatusMessage>
      )}

      <div className="pc-actions">
        <button
          type="button"
          className="pc-btn-secondary"
          disabled={!!busy || !enabled}
          onClick={testConnection}
          data-testid="shared-calendar-test"
        >
          {busy === 'test' ? 'Testing…' : 'Test connection'}
        </button>
        <button
          type="button"
          className="pc-btn-secondary"
          disabled={!!busy || !enabled}
          onClick={previewBackfill}
          data-testid="shared-calendar-preview"
        >
          {busy === 'preview' ? 'Checking…' : 'Preview back-fill'}
        </button>
      </div>

      {probe && (
        <StatusMessage as="p" variant="empty" className="pc-state" data-testid="shared-calendar-probe">
          {probe.reachable
            ? `Reached ${probe.resolvedUpn || probe.mailbox}${probe.displayName ? ` (${probe.displayName})` : ''}. This checks the calendar's own details, never anything in it.`
            : `Not reachable. ${probe.detail || probe.reason}`}
        </StatusMessage>
      )}

      {proposal && (
        <div data-testid="shared-calendar-proposal">
          <StatusMessage as="p" variant="empty" className="pc-state">
            {proposal.candidateCount === 0
              ? 'Nothing to back-fill - every approved leave that has not yet ended is already on the calendar.'
              : `${proposal.candidateCount} approved leave request${proposal.candidateCount === 1 ? '' : 's'} would be added to ${proposal.mailbox}. Leave already in the past is deliberately left out.`}
          </StatusMessage>
          {proposal.truncated && <StatusMessage as="p" variant="error" className="pc-state pc-state--error">{proposal.note}</StatusMessage>}
          {proposal.candidateCount > 0 && (
            <>
              <ul className="pc-list">
                {proposal.candidates.map((c) => (
                  <li key={c.leaveRequestId}>
                    {`${c.displayName || 'Unnamed employee'}: ${c.startDate} to ${c.endDate}`}
                  </li>
                ))}
              </ul>
              <div className="pc-actions">
                <button
                  type="button"
                  className="pc-btn-primary"
                  disabled={!!busy}
                  onClick={commitBackfill}
                  data-testid="shared-calendar-commit"
                >
                  {busy === 'commit' ? 'Adding…' : `Add these ${proposal.candidateCount} to the calendar`}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
